/**
 * notifyReadError — shared error toast for a failed read / data-fetch (GET).
 *
 * frontend/CLAUDE.md §4.6 says every server request must toast on both success
 * and error. For READS we deliberately deviate on the *success* half: a screen
 * routinely fires several GETs on mount, so a success toast per load would be
 * pure noise. Reads therefore stay silent on success and surface only
 * FAILURES — through this helper — while mutations (POST/PUT/PATCH/DELETE)
 * keep toasting on both success and error at their own call sites.
 *
 * This helper is read-only: do NOT use it for mutations (they already toast
 * success + error individually). Callers may still render an inline
 * error/empty fallback in the page body; this is the *additional* toast the
 * consistency rule requires, not a replacement for the inline state.
 *
 * Accepts whatever the caller has on hand: a raw caught error (`unknown` /
 * `Error` / `ServiceError`) from a `.catch`, or an already-extracted message
 * string (e.g. DataTable's `error` prop). The backend's human-readable
 * `detail.message` is surfaced by `apiClient` as `Error.message`, so passing
 * the caught error preserves the specific server message.
 */
import { showToast } from "./toast";

/** Shown when the error carries no usable message (non-Error throw, empty message). */
export const DEFAULT_READ_ERROR_MESSAGE =
  "Something went wrong while loading. Please try again.";

function readErrorMessage(error: unknown): string {
  // Already-extracted message string (e.g. DataTable's `error` prop).
  if (typeof error === "string" && error.trim()) return error;
  // Raw caught error — apiClient puts the backend message on Error.message.
  if (error instanceof Error && error.message) return error.message;
  return DEFAULT_READ_ERROR_MESSAGE;
}

export function notifyReadError(error: unknown): void {
  showToast({
    variant: "error",
    title: "Failed to load",
    description: readErrorMessage(error),
  });
}
